import React, { Children } from 'react';
import PropTypes from 'prop-types';
import Media from '../Media';
import MediaFrame from '../MediaFrame';
import { classNamesFor } from '../utils/classNames';
var cx;
/**
 * The MultiImage component composes several images into a mosaic of images.
 * The MultiImage produces an image that has an aspect ratio of 1:1. MultiImage
 * can be composed of any component which has src and alt props. Examples are:
 * - Image
 * - Media
 * - AvatarIcon
 *
 * AvatarIcon can also show multiple user images and should be used in favor of
 * MultiImage when used to show user identity related images.
 *
 * ## Related Components
 * - [Image](../Image/README.md)
 * - [Media](../Media/README.md)
 * - [AvatarIcon](../AvatarIcon/README.md)
 *
 * ## Composed of
 * - [MediaFrame](../MediaFrame/README.md)
 * - [Media](../Media/README.md)
 *
 * ## Usage
 *
 * ```jsx
 * <MultiImage size="small" maxFaces={4}>
 *    <Image
 *       src="http://profilePicOrAvatar/image1.webp"
 *       alt="So and so's image"
 *    />
 *    <Image
 *       src="http://profilePicOrAvatar/image2.webp"
 *       alt="So and so's image"
 *    />
 *    <Image
 *       src="http://profilePicOrAvatar/image3.webp"
 *       alt="So and so's image"
 *    />
 *    <Image
 *       src="http://profilePicOrAvatar/image4.webp"
 *       alt="So and so's image"
 *    />
 * </AvatarIcon>
 *
 *```
 * ## Style Hooks
 *
 * - **root** the root element
 *
 * ## Props
 */

var MultiImage = function MultiImage(props) {
  var qa = props.qa,
      className = props.className,
      width = props.width,
      children = props.children;
  var images = Children.toArray(children).slice(0, 4).map(function (x) {
    return {
      src: x.props.src,
      alt: x.props.alt,
      children: x.props.children
    };
  });
  var imageCount = images.length;
  var positions = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];

  var getWidth = function getWidth(i) {
    if (imageCount === 1 || imageCount === 2) return '100%';
    if (imageCount === 4) return '50%';
    if (i === 0) return '100%';
    return '50%';
  };

  var getClipPath = function getClipPath(i) {
    if ((imageCount === 2 || imageCount === 3) && i === 0) {
      return 'polygon(0 0, 50% 0, 50% 100%, 0 100%)';
    }

    if (imageCount === 2 && i === 1) {
      return 'polygon(50% 0, 100% 0, 100% 100%, 50% 100%)';
    }

    return null;
  };

  var _MultiImage$getClassN = MultiImage.getClassName({
    className: className
  }),
      root = _MultiImage$getClassN.root;

  return /*#__PURE__*/React.createElement(MediaFrame, {
    qa: qa,
    className: root,
    aspectRatio: "1:1",
    width: width
  }, images.map(function (p, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: p.alt,
      className: "psw-".concat(positions[i], " psw-clip"),
      style: {
        width: getWidth(i),
        height: getWidth(i),
        clipPath: getClipPath(i)
      }
    }, /*#__PURE__*/React.createElement(Media, {
      qa: "".concat(qa, "#image").concat(i),
      type: "image",
      aspectRatio: "1:1",
      width: "100%",
      src: p.src,
      alt: p.alt
    }, p.children));
  }));
};

MultiImage.getClassName = function (_ref) {
  var className = _ref.className;
  return {
    root: cx('psw-multi-image', className)
  };
};

MultiImage.useClassNames = function (x) {
  MultiImage.classNames = x;
};

MultiImage.defaultProps = {
  qa: '',
  className: '',
  width: 50,
  children: null
};
MultiImage.propTypes = {
  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Used to pass in the images to compose. See usage above. Accepts
   * Image, Media, AvatarIcon or any component with src and alt props.
   * In the case of greeking before data is available, children is optional to
   * save the space for pending images.
   */
  children: PropTypes.node
};
cx = classNamesFor(MultiImage);
export default MultiImage;